/**
 * @packageDocumentation
 * @module f1/asynchronous
 */

import fold from '../adt/fold';
import task, { TTask } from './task';

export interface Serial {
  /**
   * Run all asynchronous tasks in serial, each much succeed to invoke the next
   *
   * @public
   * @return an array of values from each task (like f1.parallel)
   * @see f1.parallel
   * @function f1.serial
   * @param tasks a list of tasks to execute
   * @example
   *
   * ```ts
   * f1.serial([
   *   () => fetch('1.json'),
   *   () => fetch('2.json')
   * ], f1.skip).then(([_1, _2]) => ({
   *   _1,
   *   _2
   * }));
   * ```
   *
   */
  <E, R>(tasks: TTask<E, R>[], value: E): Promise<unknown>;
  <E, R>(tasks: TTask<E, R>[]): (value: E) => Promise<unknown>;
}

/**
 * @see Serial
 */
export default <E, R>(tasks: TTask<E, R>[], value: E): Promise<unknown[]> => {
  return new Promise((resolve) => {
    const rtn: unknown[] = [];

    fold<TTask<E, R>, Promise<unknown>, Promise<unknown[]>>(
      (chain, t) =>
        chain.then((v) => {
          rtn.push(v);
          return task(t, value);
        }),
      Promise.resolve(undefined) as Promise<unknown>,
      tasks
    ).then((v) => {
      // if we give serial an empty array we should not touch the array
      if (tasks.length > 0) {
        rtn.shift();
        rtn.push(v);
      }

      resolve(rtn);
    });
  });
};
